<template>
  <BModal
    :model-value="show"
    size="xl"
    title="収集カレンダー設定"
    ok-only
    ok-title="閉じる"
    scrollable
    no-close-on-backdrop
    no-close-on-esc
    @update:model-value="handleClose"
    @ok="handleClose"
  >
    <!-- 子モーダル用のオーバーレイ -->
    <div v-if="hasChildModal" class="child-modal-overlay"></div>
    
    <!-- ヘッダー部分 -->
    <div class="calendar-header mb-3">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="district-info">
          <strong>収集地区:</strong> {{ district?.districtName || '' }}
        </div>
        <MonthSelector
          :year="year"
          :month="month"
          @change="handleMonthChange"
        />
        <BaseButton
          variant="primary"
          size="sm"
          @click="showBasicPatternModal"
        >
          基本曜日設定
        </BaseButton>
      </div>
    </div>

    <!-- カレンダー本体 -->
    <div class="calendar-grid">
      <!-- 曜日ヘッダー -->
      <div class="calendar-header-row">
        <div
          v-for="day in weekDays"
          :key="day"
          class="calendar-header-cell"
          :class="{ 'sunday': day === '日', 'saturday': day === '土' }"
        >
          {{ day }}
        </div>
      </div>

      <!-- カレンダー日付 -->
      <div class="calendar-body">
        <CalendarDayCell
          v-for="day in calendarDays"
          :key="day.date"
          :day="day"
          :selected="isSelected(day)"
          @click="handleDayClick(day)"
        />
      </div>
    </div>
  </BModal>
</template>

<script setup>
import { ref, computed } from 'vue'
import { BModal } from 'bootstrap-vue-next'
import BaseButton from '@/components/atoms/BaseButton.vue'
import MonthSelector from '@/components/molecules/MonthSelector.vue'
import CalendarDayCell from './CalendarDayCell.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  district: {
    type: Object,
    default: null
  },
  year: {
    type: Number,
    required: true
  },
  month: {
    type: Number,
    required: true
  },
  calendarData: {
    type: Object,
    default: () => ({})
  },
  hasChildModal: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'month-change', 'day-click', 'open-pattern-settings'])

const weekDays = ['日', '月', '火', '水', '木', '金', '土']
const CALENDAR_TOTAL_CELLS = 7 * 6 // 7日 × 6週 = 42マス
const selectedDay = ref(null)

const calendarDays = computed(() => {
  return generateCalendarDays(props.year, props.month)
})

const handleClose = () => {
  emit('close')
}

const handleMonthChange = ({ year, month }) => {
  emit('month-change', { year, month })
}

const handleDayClick = (day) => {
  if (!day.isCurrentMonth) return
  selectedDay.value = day.date
  emit('day-click', day)
}

const isSelected = (day) => {
  return selectedDay.value === day.date
}

const showBasicPatternModal = () => {
  emit('open-pattern-settings')
}

const generateCalendarDays = (year, month) => {
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0)
  const firstDayOfWeek = firstDay.getDay()
  const daysInMonth = lastDay.getDate()

  const days = []

  // 前月の日付
  const prevMonth = month === 1 ? 12 : month - 1
  const prevYear = month === 1 ? year - 1 : year
  const prevMonthLastDay = new Date(prevYear, prevMonth, 0).getDate()

  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const dayNumber = prevMonthLastDay - i
    const date = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`
    days.push({
      date,
      dayNumber,
      isCurrentMonth: false,
      wasteTypes: props.calendarData[date] || []
    })
  }

  // 当月の日付
  for (let i = 1; i <= daysInMonth; i++) {
    const date = `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`
    days.push({
      date,
      dayNumber: i,
      isCurrentMonth: true,
      wasteTypes: props.calendarData[date] || []
    })
  }

  // 翌月の日付（6週間分まで埋める）
  const remainingCells = CALENDAR_TOTAL_CELLS - days.length
  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear = month === 12 ? year + 1 : year

  for (let i = 1; i <= remainingCells; i++) {
    const date = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(i).padStart(2, '0')}`
    days.push({
      date,
      dayNumber: i,
      isCurrentMonth: false,
      wasteTypes: props.calendarData[date] || []
    })
  }

  return days
}
</script>

<style lang="scss" scoped>
.calendar-header {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
}

.district-info {
  font-size: 1.1rem;
  color: #374151;
}

.calendar-grid {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
}

.calendar-header-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e5e7eb;
}

.calendar-header-cell {
  padding: 0.75rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
  text-transform: uppercase;
  border-right: 1px solid #e5e7eb;

  &:last-child {
    border-right: none;
  }

  &.sunday {
    color: #dc2626;
  }

  &.saturday {
    color: #2563eb;
  }
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #ffffff;
}

@media (max-width: 768px) {
  .calendar-header-cell {
    padding: 0.5rem;
    font-size: 0.75rem;
  }

  .district-info {
    font-size: 1rem;
  }
}
</style>

<style>
/* 子モーダル用のオーバーレイ */
.child-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  pointer-events: none;
}
</style>
